import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, History, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { PAYSTUB_TEMPLATES } from '../TemplateGallery';
import PaystubRedatePanel from './PaystubRedatePanel';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pretty = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  return y ? new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
};
const templateName = (id) => PAYSTUB_TEMPLATES.find((t) => t.id === Number(id))?.name || `Template ${id ?? 1}`;

const DETAIL_KEYS = [
  ['grossPay', 'Gross'], ['federalTax', 'Federal'], ['stateTax', 'State'], ['socialSecurity', 'Social Sec.'],
  ['medicare', 'Medicare'], ['sdi', 'SDI'], ['otherDeductions', 'Other ded.'], ['netPay', 'Net'],
];

const StubDetails = ({ stub }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs" data-testid="stub-history-details">
    {DETAIL_KEYS.map(([k, label]) => (
      <div key={k} className="rounded-md bg-zinc-950 border border-zinc-800 px-2 py-1.5">
        <p className="text-zinc-500">{label}</p>
        <p className="text-white font-mono">{money(stub.current?.[k])}</p>
        <p className="text-zinc-600 font-mono">YTD {money(stub.ytd?.[k])}</p>
      </div>
    ))}
  </div>
);

const StubRow = ({ stub, index, selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-lg border p-3 ${selected ? 'border-sky-500 bg-sky-500/10' : 'border-zinc-800 bg-zinc-900'}`} data-testid={`stub-history-row-${index}`}>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={onSelect} className="flex-1 min-w-[200px] text-left" data-testid={`stub-history-select-${index}`}>
          <p className="text-white font-semibold">{pretty(stub.payDate)} {index === 0 && <span className="ml-2 text-[10px] uppercase text-sky-300">Latest</span>}</p>
          <p className="text-xs text-zinc-400">{pretty(stub.periodStart)} – {pretty(stub.periodEnd)} · Check #{stub.checkNumber || '—'} · {templateName(stub.template_id)}</p>
        </button>
        <div className="text-right text-xs">
          <p className="text-zinc-300 font-mono">Gross {money(stub.current?.grossPay)}</p>
          <p className="text-emerald-300 font-mono">Net {money(stub.current?.netPay)}</p>
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-zinc-400 hover:text-white" data-testid={`stub-history-toggle-${index}`}>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      {open && <StubDetails stub={stub} />}
    </div>
  );
};

export default function ClientStubHistory({ client, onBack, onApply }) {
  const [stubs, setStubs] = useState(null);
  const [source, setSource] = useState('history');
  const [baseIdx, setBaseIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/api/paystub-clients/${client.id}/stub-history`)
      .then((r) => { setStubs(r.data.stubs || []); setSource(r.data.source); })
      .catch(() => { setStubs(client.formData?.paystubs || []); setSource('last_saved'); });
  }, [client]);

  const base = stubs?.[baseIdx];
  const fd = client.formData || {};
  const redateSource = base && {
    formData: {
      ...fd,
      payFrequency: base.pay_frequency || fd.payFrequency,
      selectedTemplate: base.template_id ?? fd.selectedTemplate ?? 1,
      paystubs: [base],
    },
    extractedData: null,
  };

  return (
    <div className="space-y-6" data-testid="client-stub-history">
      <button type="button" onClick={onBack} className="ml-12 md:ml-0 text-zinc-400 hover:text-white text-sm flex items-center gap-2" data-testid="stub-history-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to clients
      </button>
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><History className="w-6 h-6 text-sky-400" /> Update latest stubs</h1>
        <p className="text-sm text-zinc-400 mt-1">{client.name} — pick the stub to continue from, then choose the new dates.</p>
        {source === 'last_saved' && <p className="text-xs text-amber-300 mt-1" data-testid="stub-history-last-saved-note">Showing the last saved stubs — full history records from now on.</p>}
      </div>

      {!stubs && <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />}
      {stubs && !stubs.length && <p className="text-sm text-zinc-500">No stubs on file for this client yet.</p>}
      {stubs?.length > 0 && (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="stub-history-list">
          {stubs.map((s, i) => (
            <StubRow key={`${s.payDate}-${i}`} stub={s} index={i} selected={i === baseIdx} onSelect={() => setBaseIdx(i)} />
          ))}
        </div>
      )}

      {redateSource && (
        <PaystubRedatePanel
          key={`${base.payDate}-${baseIdx}`}
          source={redateSource}
          onBack={onBack}
          onApply={onApply}
          modes={['next', 'latest', 'specific']}
          initialMode="next"
          maxCount={6}
          templateName={templateName(redateSource.formData.selectedTemplate)}
          showHeader={false}
        />
      )}
    </div>
  );
}
